import { Trans, useLingui } from '@lingui/react/macro';

import VipBadge from '@/components/VipBadge';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconClock from '@/icons/Clock.svg';
import IconDownload from '@/icons/Download.svg';
import IconEmail from '@/icons/Email.svg';
import IconInfo from '@/icons/Info.svg';
import IconLanguage from '@/icons/Language.svg';
import IconPhone from '@/icons/Phone.svg';
import IconPlace from '@/icons/Place.svg';
import IconReceipt from '@/icons/Receipt.svg';
import IconStore from '@/icons/Store.svg';
import IconTruck from '@/icons/Truck.svg';
import IconPerson from '@/icons/User.svg';
import { Address, formatStreet, formatZip } from '@/models/address';
import { DeliveryOption } from '@/models/client';
import Phone, { formatPhone } from '@/models/partials/phone';
import { Locale, SUPPORTED_LOCALES } from '@/services/i18n';

type ContactDetailsProps = {
  type?: 'client' | 'store';
  name?: string | null;
  role?: string;
  email?: string | null;
  addressName?: string | null;
  address?: Address | null;
  openingHours?: string | null;
  phone?: Phone | null;
  deliveryChoice?: DeliveryOption | null;
  locale?: Locale | null;
  documents?: { label: string; url: string }[];
  size?: 'small' | 'medium';
  isVIP?: boolean;
  placeIcon?: 'start' | 'end' | null;
  addressFirst?: boolean;
};

const ContactDetails = ({
  type = 'client',
  name,
  role,
  email,
  addressName,
  address,
  openingHours,
  phone,
  deliveryChoice,
  locale,
  documents,
  size = 'medium',
  isVIP,
  placeIcon = 'end',
  addressFirst = false,
}: ContactDetailsProps) => {
  const { i18n } = useLingui();

  const Icon = {
    client: IconPerson,
    store: IconStore,
  }[type];

  const textSize = size === 'small' ? '200' : '100';
  const iconSize = size === 'small' ? '1rem' : '1.25rem';

  return (
    <Stack gap={size === 'small' ? '0.5rem' : '0.25rem'} className="sentry-mask">
      {addressFirst && (
        <ContactDetailsAddress
          address={address}
          addressName={addressName}
          placeIcon={placeIcon}
          iconSize={iconSize}
          textSize={textSize}
        />
      )}

      {(name || role) && (
        <Stack row flexWrap="nowrap" gap="0.5rem">
          <Icon style={{ fontSize: iconSize }} />
          <div>
            <Stack row flexWrap="nowrap" gap="0.5rem">
              <Paragraph size={textSize} weight="medium">
                {name}
              </Paragraph>
              {isVIP && <VipBadge />}
            </Stack>
            {role && (
              <Paragraph size="200" style={{ paddingBottom: '0.25rem' }}>
                {role}
              </Paragraph>
            )}
          </div>
        </Stack>
      )}
      {email && (
        <Stack row flexWrap="nowrap" gap="0.5rem">
          <IconEmail style={{ fontSize: iconSize }} />
          <Paragraph size={textSize} style={{ overflow: 'auto' }}>
            {email}
          </Paragraph>
        </Stack>
      )}

      {phone?.countryCode && phone?.number && (
        <Stack row flexWrap="nowrap" gap="0.5rem" alignItems="center">
          <IconPhone style={{ fontSize: iconSize }} />
          <Paragraph size={textSize}>{formatPhone(phone)}</Paragraph>
        </Stack>
      )}

      {!addressFirst && (
        <ContactDetailsAddress
          address={address}
          addressName={addressName}
          placeIcon={placeIcon}
          iconSize={iconSize}
          textSize={textSize}
        />
      )}

      {openingHours && (
        <Stack row flexWrap="nowrap" gap="0.5rem">
          <IconClock style={{ fontSize: iconSize }} />
          <Stack gap="0.25rem">
            <Stack row gap="0.25rem" alignItems="center">
              <Paragraph size={textSize} weight="medium">
                <Trans id="contact-details.opening-hours">Opening hours</Trans>
              </Paragraph>{' '}
              <Tooltip
                content={
                  <Trans id="contact-details.opening-hours.bank-holidays">
                    Opening hours may differ during bank holidays.
                  </Trans>
                }
              >
                <Button variant="style-less">
                  <IconInfo className="text-disabled" style={{ fontSize: '0.75rem' }} />
                </Button>
              </Tooltip>
            </Stack>
            <Paragraph size={textSize} whiteSpace="pre-wrap">
              {openingHours}
            </Paragraph>
          </Stack>
        </Stack>
      )}

      {deliveryChoice && (
        <Stack row flexWrap="nowrap" gap="0.5rem" alignItems="center">
          <IconTruck style={{ fontSize: iconSize }} />
          <Paragraph size={textSize}>
            {deliveryChoice === 'store' && (
              <Trans id="contact-details.delivery.in-store">Shipping back in store</Trans>
            )}
            {deliveryChoice === 'home' && (
              <Trans id="contact-details.delivery.at-home">Shipping back at home</Trans>
            )}
            {deliveryChoice === 'pickupPoint_mondialRelay' && (
              <Trans id="contact-details.delivery.pickup-point">
                Shipping back in pickup point (Mondial Relay)
              </Trans>
            )}
          </Paragraph>
        </Stack>
      )}

      {locale && (
        <Stack row flexWrap="nowrap" gap="0.5rem" alignItems="center">
          <IconLanguage style={{ fontSize: iconSize }} />
          <Paragraph size={textSize}>
            {i18n._(SUPPORTED_LOCALES.find((l) => l.id === locale)!.label)}
          </Paragraph>
        </Stack>
      )}

      {documents?.map((document) => (
        <Stack
          row
          flexWrap="nowrap"
          gap="0.5rem"
          alignItems="center"
          key={document.url}
          // eslint-disable-next-line lingui/no-unlocalized-strings
          style={{ margin: '-0.25rem 0' }}
        >
          <IconReceipt style={{ fontSize: iconSize }} />
          <Paragraph size={textSize}>{document.label}</Paragraph>
          <Button variant="secondary" size="small" iconOnly href={document.url} download>
            <IconDownload />
          </Button>
        </Stack>
      ))}
    </Stack>
  );
};

const ContactDetailsAddress = ({
  address,
  addressName,
  placeIcon,
  iconSize,
  textSize,
}: ContactDetailsProps & { iconSize: '1rem' | '1.25rem'; textSize?: '100' | '200' }) => {
  if (!address || !(addressName || formatStreet(address) || formatZip(address))) {
    return null;
  }

  return (
    <Stack row flexWrap="nowrap" gap="0.5rem">
      <IconPlace
        end={placeIcon === 'end'}
        start={placeIcon === 'start'}
        style={{
          fontSize: iconSize,
          marginTop: addressName ? '0.125rem' : undefined,
        }}
      />
      <div>
        {addressName && (
          <Paragraph size="100" weight="medium">
            {addressName}
          </Paragraph>
        )}
        {(formatStreet(address) || formatZip(address)) && (
          <>
            <Paragraph size={textSize}>{formatStreet(address)}</Paragraph>
            <Paragraph size={textSize}>{formatZip(address)}</Paragraph>
          </>
        )}
      </div>
    </Stack>
  );
};

export default ContactDetails;
